import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Bookmark,
  BookmarkCheck,
  Check,
  ChevronsUpDown,
  Clock,
  Compass,
  Footprints,
  Leaf,
  MapPin,
  Mountain,
  Navigation,
  RefreshCw,
  Route as RouteIcon,
  Sparkles,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import TrailMap from "./TrailMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVITIES = ["Hiking", "Trail Running", "Bird Watching", "Scenic Walk"];
const VIBES = ["Shady Forests", "Open Meadows", "Lakeside Views", "Autumn/Fall Foliage"];
const REGIONS = [
  "India (South Asia)",
  "North America",
  "Europe",
  "East Asia",
  "Global / Anywhere Else",
];
const STATUSES = [
  "Reading the wind…",
  "Consulting local birds…",
  "Measuring shade coverage…",
  "Waking up the trail…",
  "Aligning the compass…",
];
const ease = [0.22, 1, 0.36, 1];

export default function RouteGenerator({
  onSaveRoute = () => false,
  isRouteSaved = () => false,
}) {
  const [activity, setActivity] = useState("Hiking");
  const [vibe, setVibe] = useState("Autumn/Fall Foliage");
  const [region, setRegion] = useState("Global / Anywhere Else");
  const [localRegion, setLocalRegion] = useState("");
  const [locating, setLocating] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [actQuery, setActQuery] = useState("");

  const pickActivity = (val) => {
    const clean = val.trim();
    if (clean) setActivity(clean);
    setActOpen(false);
    setActQuery("");
  };
  const [loading, setLoading] = useState(false);
  const [statusIdx, setStatusIdx] = useState(0);
  const [route, setRoute] = useState(null);
  const [done, setDone] = useState([false, false, false]);
  const [count, setCount] = useState(null);

  useEffect(() => {
    if (!loading) return;
    const t = setInterval(() => setStatusIdx((i) => (i + 1) % STATUSES.length), 750);
    return () => clearInterval(t);
  }, [loading]);

  const findRoutes = async () => {
    setLoading(true);
    setRoute(null);
    setDone([false, false, false]);
    const t0 = Date.now();
    try {
      const { data } = await axios.post(`${API}/routes/generate`, {
        activity,
        vibe,
        region,
        local_region: localRegion.trim(),
      });
      const wait = Math.max(0, 1400 - (Date.now() - t0));
      setTimeout(() => {
        setRoute(data);
        setLoading(false);
        axios
          .get(`${API}/routes/count`)
          .then((r) => setCount(r.data.count))
          .catch(() => {});
      }, wait);
    } catch (e) {
      setLoading(false);
      toast.error("The trail went cold — try again.");
    }
  };

  const toggleTask = (i) => {
    const next = done.map((d, idx) => (idx === i ? !d : d));
    setDone(next);
    if (next.every(Boolean)) {
      toast.success("All 3 tasks done. That's a proper Touch Grass day.");
    }
  };

  const regionFromCoords = (lat, lon) => {
    if (lat >= 5 && lat <= 38 && lon >= 65 && lon <= 100) return "India (South Asia)";
    if (lon > 100 && lat <= 65) return "East Asia";
    if (lon < -30) return "North America";
    if (lat > 34) return "Europe";
    return "Global / Anywhere Else";
  };

  const detectRegion = () => {
    if (!navigator.geolocation) {
      toast.error("Location not available — pick a region instead.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const detected = regionFromCoords(coords.latitude, coords.longitude);
        setRegion(detected);
        setLocating(false);
        toast.success(`Region set to ${detected} — trails updated.`);
      },
      () => {
        setLocating(false);
        toast.error("Location access denied — pick a region instead.");
      },
      { timeout: 8000 },
    );
  };

  const handleSave = () => {
    if (!route) return;
    if (onSaveRoute(route)) toast.success("Saved to your trail journal.");
  };

  const doneCount = done.filter(Boolean).length;

  return (
    <section id="routes" className="relative scroll-mt-20 px-5 py-24 md:px-12 md:py-32">
      <div className="mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, ease }}
          className="mb-12 flex flex-col gap-6 md:flex-row md:items-end md:justify-between"
        >
          <div>
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-moss">
              <RouteIcon className="h-4 w-4" /> The Route Generator
            </p>
            <h2 className="mt-3 font-display text-3xl tracking-tight text-cream sm:text-4xl lg:text-5xl">
              Tell the forest your mood.
              <br />
              <span className="italic text-forest-300">It draws the trail.</span>
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-forest-dim">
            Pick an activity and a vibe. Our trail conjurer (very real backend, very fake AI)
            draws up a route, a hand-inked map, and three tiny missions.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.7, delay: 0.1, ease }}
          className="rounded-3xl border border-forest-600/60 bg-forest-900/70 p-6 backdrop-blur-md md:p-8"
        >
          <div className="grid gap-5 md:grid-cols-2 md:items-end lg:grid-cols-[1fr_1fr_1fr_auto]">
            <div>
              <div className="mb-2 flex items-center justify-between gap-2">
                <label className="block font-mono text-[11px] uppercase tracking-[0.2em] text-forest-300">
                  Select Your Region
                </label>
                <button
                  data-testid="detect-region-button"
                  onClick={detectRegion}
                  disabled={locating}
                  className="flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.15em] text-moss transition-colors hover:text-cream disabled:opacity-60"
                >
                  {locating ? (
                    <RefreshCw className="h-3 w-3 animate-spin" />
                  ) : (
                    <Navigation className="h-3 w-3" />
                  )}
                  Detect
                </button>
              </div>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger
                  data-testid="region-select"
                  className="h-12 w-full rounded-xl border-forest-500/50 bg-forest-800/80 text-cream transition-colors hover:border-moss/60"
                >
                  <SelectValue placeholder="Pick a region" />
                </SelectTrigger>
                <SelectContent className="border-forest-600 bg-forest-800 text-cream">
                  {REGIONS.map((r) => (
                    <SelectItem key={r} value={r}>
                      {r}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-forest-300">
                Activity
              </label>
              <div className="relative">
                <Input
                  data-testid="activity-select"
                  role="combobox"
                  aria-expanded={actOpen}
                  value={actOpen ? actQuery : activity}
                  onFocus={() => {
                    setActQuery("");
                    setActOpen(true);
                  }}
                  onChange={(e) => {
                    setActQuery(e.target.value);
                    setActOpen(true);
                  }}
                  onBlur={() => setTimeout(() => setActOpen(false), 150)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      if (actOpen && actQuery.trim()) pickActivity(actQuery);
                      else {
                        setActOpen(false);
                        findRoutes();
                      }
                    }
                    if (e.key === "Escape") setActOpen(false);
                  }}
                  placeholder="Pick or type an activity..."
                  className="h-12 w-full rounded-xl border-forest-500/50 bg-forest-800/80 pr-9 text-cream placeholder:text-forest-muted focus-visible:border-moss/70 focus-visible:ring-moss/30 focus-visible:ring-offset-forest-950"
                />
                <ChevronsUpDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-forest-muted" />
                {actOpen && (
                  <div className="absolute z-40 mt-2 w-full overflow-hidden rounded-xl border border-forest-600 bg-forest-800 shadow-2xl">
                    <div className="max-h-56 overflow-y-auto p-1.5">
                      {ACTIVITIES.filter((a) =>
                        a.toLowerCase().includes(actQuery.trim().toLowerCase())
                      ).map((a) => (
                        <button
                          key={a}
                          data-testid={`activity-option-${a.toLowerCase().replace(/\s+/g, "-")}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            pickActivity(a);
                          }}
                          className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-cream transition-colors hover:bg-forest-700"
                        >
                          {a}
                          {a === activity && <Check className="ml-auto h-4 w-4 text-moss" />}
                        </button>
                      ))}
                      {actQuery.trim() &&
                        !ACTIVITIES.some(
                          (a) => a.toLowerCase() === actQuery.trim().toLowerCase()
                        ) && (
                          <button
                            data-testid="activity-custom-option"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              pickActivity(actQuery);
                            }}
                            className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm text-moss transition-colors hover:bg-forest-700"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            Use “{actQuery.trim()}”
                          </button>
                        )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-forest-300">
                Vibe
              </label>
              <Select value={vibe} onValueChange={setVibe}>
                <SelectTrigger
                  data-testid="vibe-select"
                  className="h-12 w-full rounded-xl border-forest-500/50 bg-forest-800/80 text-cream transition-colors hover:border-moss/60"
                >
                  <SelectValue placeholder="Pick a vibe" />
                </SelectTrigger>
                <SelectContent className="border-forest-600 bg-forest-800 text-cream">
                  {VIBES.map((v) => (
                    <SelectItem key={v} value={v}>
                      {v}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <button
              data-testid="find-routes-button"
              onClick={findRoutes}
              disabled={loading}
              className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-moss px-7 font-semibold text-forest-950 transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 md:min-w-[220px]"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span className="truncate">{STATUSES[statusIdx]}</span>
                </>
              ) : (
                <>
                  <Compass className="h-4 w-4" />
                  Find Routes
                </>
              )}
            </button>

            <div className="md:col-span-2 lg:col-span-4">
              <label
                htmlFor="local-region"
                className="mb-2 block font-mono text-[11px] tracking-[0.1em] text-forest-300"
              >
                Enter your State or City (e.g., Maharashtra, Karnataka, California)
              </label>
              <Input
                id="local-region"
                data-testid="local-region-input"
                value={localRegion}
                onChange={(e) => setLocalRegion(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && findRoutes()}
                placeholder="Type your local region..."
                className="h-12 w-full rounded-xl border-forest-500/50 bg-forest-800/80 text-cream placeholder:text-forest-muted focus-visible:border-moss/70 focus-visible:ring-moss/30 focus-visible:ring-offset-forest-950"
              />
              <p
                data-testid="local-region-note"
                className="mt-2 flex items-center gap-1.5 text-xs text-forest-muted"
              >
                <Leaf className="h-3.5 w-3.5 text-moss" />
                Finding micro-adventures close to home to reduce travel time.
              </p>
            </div>
          </div>
        </motion.div>

        <AnimatePresence mode="wait">
          {loading && (
            <motion.div
              key="loading"
              data-testid="loading-card"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease }}
              className="mt-10 rounded-3xl border border-forest-600/50 bg-forest-900/60 p-8 md:p-12"
            >
              <div className="shimmer-bg animate-shimmer mb-6 h-9 w-2/3 rounded-lg" />
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-3">
                  <div className="shimmer-bg animate-shimmer h-4 w-full rounded" />
                  <div className="shimmer-bg animate-shimmer h-4 w-5/6 rounded" />
                  <div className="shimmer-bg animate-shimmer h-4 w-2/3 rounded" />
                </div>
                <div className="shimmer-bg animate-shimmer h-40 rounded-xl" />
              </div>
              <AnimatePresence mode="wait">
                <motion.p
                  key={statusIdx}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-forest-300"
                >
                  {STATUSES[statusIdx]}
                </motion.p>
              </AnimatePresence>
            </motion.div>
          )}

          {route && !loading && (
            <motion.div
              key="result"
              data-testid="results-card"
              initial={{ opacity: 0, y: 40, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.7, ease }}
              className="mt-10 overflow-hidden rounded-3xl border border-forest-500/40 bg-forest-800/80 shadow-[0_0_60px_rgba(82,183,136,0.08)]"
            >
              <div className="grid lg:grid-cols-[1.1fr_0.9fr]">
                <div className="border-b border-forest-600/50 p-7 md:p-10 lg:border-b-0 lg:border-r">
                  <div className="flex flex-wrap gap-2">
                    <span className="rounded-full border border-clay/40 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-clay">
                      {route.vibe}
                    </span>
                    <span className="rounded-full border border-moss/40 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-moss">
                      {route.activity}
                    </span>
                    <span className="rounded-full border border-forest-500/50 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-forest-dim">
                      {route.difficulty}
                    </span>
                    <span
                      data-testid="region-badge"
                      className="rounded-full border border-ember/40 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-ember"
                    >
                      {route.region}
                    </span>
                    {route.local_region && (
                      <span
                        data-testid="local-badge"
                        className="flex items-center gap-1 rounded-full border border-moss/50 bg-moss/10 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.15em] text-moss"
                      >
                        <MapPin className="h-3 w-3" />
                        near {route.local_region}
                      </span>
                    )}
                  </div>
                  <h3
                    data-testid="route-name"
                    className="mt-5 font-display text-3xl tracking-tight text-cream md:text-4xl"
                  >
                    {route.name}
                  </h3>
                  <p
                    data-testid="route-tagline"
                    className="mt-2 font-display text-lg italic text-forest-300"
                  >
                    “{route.tagline}”
                  </p>
                  <div
                    data-testid="route-meta"
                    className="mt-6 flex flex-wrap gap-x-8 gap-y-3 font-mono text-xs text-forest-dim"
                  >
                    <span className="flex items-center gap-2">
                      <Footprints className="h-4 w-4 text-moss" /> {route.distance_km} km
                    </span>
                    <span className="flex items-center gap-2">
                      <Mountain className="h-4 w-4 text-moss" /> ↑ {route.elevation_m} m
                    </span>
                    <span className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-moss" /> ~{route.duration_min} min
                    </span>
                  </div>
                  <TrailMap
                    trailKey={route.trail_key}
                    waypoints={route.waypoints}
                    vibe={route.vibe}
                    name={route.name}
                  />
                </div>

                <div className="flex flex-col p-7 md:p-10">
                  <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-moss">
                    Field tasks
                  </p>
                  <h4 className="mt-3 font-display text-2xl text-cream">
                    {route.local_region
                      ? `Do these outside in ${route.local_region}.`
                      : "Do these outside. Yes, actually outside."}
                  </h4>

                  <div className="mt-6">
                    {route.tasks.map((task, i) => (
                      <button
                        key={i}
                        data-testid={`task-checkbox-${i}`}
                        onClick={() => toggleTask(i)}
                        className="group flex w-full items-start gap-4 border-b border-forest-600/40 py-4 text-left last:border-0"
                      >
                        <span
                          className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-all ${
                            done[i]
                              ? "border-moss bg-moss"
                              : "border-forest-500 group-hover:border-moss"
                          }`}
                        >
                          {done[i] && (
                            <Check className="h-4 w-4 text-forest-950" strokeWidth={3} />
                          )}
                        </span>
                        <span
                          className={`transition-all ${
                            done[i] ? "task-done text-forest-muted" : "text-cream/90"
                          }`}
                        >
                          {task.text}
                          <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.15em] text-forest-muted">
                            {task.flavor}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>

                  <div className="mt-6">
                    <div className="h-1.5 overflow-hidden rounded-full bg-forest-600/50">
                      <motion.div
                        className="h-full rounded-full bg-moss"
                        animate={{ width: `${(doneCount / 3) * 100}%` }}
                        transition={{ duration: 0.5, ease }}
                      />
                    </div>
                    <p
                      data-testid="task-progress"
                      className="mt-2 font-mono text-[11px] uppercase tracking-[0.15em] text-forest-dim"
                    >
                      {doneCount} of 3 tasks complete
                    </p>
                  </div>

                  <div className="mt-auto flex flex-wrap items-center justify-between gap-3 pt-8">
                    <div className="flex flex-wrap gap-3">
                      <button
                        data-testid="save-route-button"
                        onClick={handleSave}
                        disabled={!route || isRouteSaved(route.id)}
                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm transition-colors ${
                          route && isRouteSaved(route.id)
                            ? "cursor-default bg-moss/15 text-moss"
                            : "border border-moss/50 text-moss hover:bg-moss/10"
                        } disabled:opacity-80`}
                      >
                        {route && isRouteSaved(route.id) ? (
                          <>
                            <BookmarkCheck className="h-4 w-4" />
                            Saved
                          </>
                        ) : (
                          <>
                            <Bookmark className="h-4 w-4" />
                            Save to journal
                          </>
                        )}
                      </button>
                      <button
                        data-testid="regenerate-button"
                        onClick={findRoutes}
                        className="inline-flex items-center gap-2 rounded-full border border-forest-500/60 px-5 py-2.5 text-sm text-cream/90 transition-colors hover:border-moss/70 hover:text-moss"
                      >
                        <RefreshCw className="h-4 w-4" />
                        Generate another
                      </button>
                    </div>
                    {count != null && (
                      <p
                        data-testid="route-count"
                        className="font-mono text-[11px] text-forest-muted"
                      >
                        Route nº {count} conjured
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
